import { useClient } from 'hooks/useClient';
import { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

let rootNode: HTMLDivElement | undefined;

/** Create a portal */
export function Portal(props: { children: ReactNode }): ReactNode {
  const root = useClient(() => {
    if (!rootNode) {
      rootNode = document.createElement('div');
      document.body.appendChild(rootNode);
    }
    return rootNode;
  });
  return root ? createPortal(<>{props.children}</>, root) : null;
}

export const PortalTo = ({
  rootId,
  children,
  styleToSet,
}: {
  children: ReactNode;
  rootId: string;
  styleToSet?: Partial<CSSStyleDeclaration>;
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [targetElement, setTargetElement] = useState<any>(null);

  useEffect(() => {
    const updateStyle = (el: HTMLElement) => {
      if (!styleToSet) {
        return;
      }

      type ApprovedStyleProperty = 'top' | 'position';
      (Object.keys(styleToSet) as ApprovedStyleProperty[]).forEach(
        (styleItem: ApprovedStyleProperty) => {
          if (!styleToSet[styleItem]) {
            return;
          }
          const value = styleToSet[styleItem];
          if (typeof value === 'string') {
            el.style[styleItem] = value;
          }
        },
      );
    };
    const existingElement = document.getElementById(rootId);
    if (existingElement) {
      updateStyle(existingElement);
      setTargetElement(existingElement);
      return;
    }
    const observer = new MutationObserver((mutations, obs) => {
      const element = document.getElementById(rootId);
      if (element) {
        updateStyle(element);
        setTargetElement(element);
        obs.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [rootId, styleToSet]);

  if (!targetElement) return null;
  return createPortal(children, targetElement);
};
